import React, { useContext, useMemo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { jsx } from 'react/jsx-runtime';
import { useLocation as useLocation$1 } from 'wouter';
import { compile, pathToRegexp } from 'path-to-regexp';
import { useSearch, useLocationProperty } from 'wouter/use-location';
import makeCachedMatcher from 'wouter/matcher';

/* eslint-disable react/jsx-props-no-spreading */
const RoutesContext = /*#__PURE__*/React.createContext(null);
const useRoutesContext = () => useContext(RoutesContext);
const propTypes = {
  children: PropTypes.node.isRequired,
  routes: PropTypes.objectOf(PropTypes.string).isRequired,
  basePath: PropTypes.string
};
const defaultProps = {
  basePath: null
};
function RoutesProvider(_ref) {
  let {
    routes,
    basePath,
    children
  } = _ref;
  const value = useMemo(() => ({
    routes,
    basePath
  }), [routes, basePath]);
  return /*#__PURE__*/jsx(RoutesContext.Provider, {
    value: value,
    children: children
  });
}
RoutesProvider.propTypes = propTypes;
RoutesProvider.defaultProps = defaultProps;

const compilers = {};
function generatePath(path, data) {
  let opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (typeof compilers[path] === 'undefined') {
    compilers[path] = compile(path, opts);
  }
  const compiler = compilers[path];
  return compiler(data);
}

const useUrlGeneratorPathToRepexp = () => {
  const {
    routes = null,
    basePath = null
  } = useRoutesContext() || {};
  const urlGenerator = useCallback((key, data, opts) => {
    const path = routes !== null ? routes[key] || null : null;
    if (path === null) {
      return null;
    }
    const url = generatePath(path, data, opts);
    return basePath !== null ? `${basePath.replace(/\/$/, '')}/${url.replace(/^\//, '')}` : url;
  }, [routes, basePath]);
  return urlGenerator;
};
var useUrlGeneratorPathToRegexp = useUrlGeneratorPathToRepexp;

const useRouteNavigate = () => {
  const url = useUrlGeneratorPathToRegexp();
  const [setLocation] = useLocation$1();
  const routeNavigate = useCallback(function (route, data) {
    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }
    return setLocation(url(route, data), ...args);
  }, [setLocation, url]);
  return routeNavigate;
};
var useRouteNavigate$1 = useRouteNavigate;

const useRoutes = () => {
  const {
    routes
  } = useRoutesContext();
  return routes;
};
var useRoutes$1 = useRoutes;

function parseLocation(location) {
  let search = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let hash = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const [pathname, searchFromPath = null] = (location || '').split('?', 2);
  const [searchWithoutHash, hashFromPath = null] = (searchFromPath || '').split('#', 2);
  return {
    pathname: pathname !== '' ? pathname : '/',
    search: search || searchWithoutHash || null,
    hash: hash || hashFromPath || null
  };
}

function useLocation() {
  const [location, setLocation] = useLocation$1();
  const search = useSearch();
  const url = useMemo(() => parseLocation(location, search), [location, search]);
  return [url, setLocation];
}

function useMemoryLocationHook() {
  const memoryLocationRef = useRef(null);
  const [, setMemoryLocation] = useState(null);
  const memoryHook = useCallback(() => {
    const location = useLocationProperty(() => {
      const newLocation = memoryLocationRef.current || '/';
      return newLocation;
    });
    return [location, newLocation => {
      memoryLocationRef.current = newLocation;
      setMemoryLocation(newLocation);
    }];
  }, [setMemoryLocation]);
  return memoryHook;
}

function createPathToRegexpMatcher(opts) {
  const convertPathToRegexp = path => {
    // eslint-disable-next-line prefer-const
    let keys = [];

    // we use original pathToRegexp package here with keys
    const regexp = pathToRegexp(path, keys, {
      strict: false,
      ...opts
    });
    return {
      keys,
      regexp
    };
  };
  const pathToRegexpMatcher = makeCachedMatcher(convertPathToRegexp);
  return (pattern, path) => {
    const {
      pathname
    } = parseLocation(path);
    return pathToRegexpMatcher(pattern, pathname);
  };
}

export { RoutesContext, RoutesProvider, createPathToRegexpMatcher, generatePath, useLocation, useMemoryLocationHook, useRouteNavigate$1 as useRouteNavigate, useRoutes$1 as useRoutes, useRoutesContext, useUrlGeneratorPathToRegexp as useUrlGenerator };
